<template>
  <nav
    ref="panelRef"
    class="cat-panel"
    :class="{ horizontal }"
    aria-label="Kategorien"
  >
    <!-- Alle-Button -->
    <button
      v-if="showAll"
      class="cat-item cat-item--all"
      :class="{ active: activeCategoryId == null }"
      data-category-id="all"
      type="button"
      title="Alle"
      @click="emit('select', null)"
    >
      <div class="cat-thumb-wrap">
        <div class="cat-thumb cat-all-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/>
            <rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>
          </svg>
        </div>
      </div>
      <span class="cat-label">Alle</span>
    </button>

    <button
      v-for="cat in categories"
      :key="cat._id"
      class="cat-item"
      :class="{ active: Number(cat._id) === activeCategoryId }"
      :data-category-id="String(cat._id)"
      type="button"
      :title="cat.title"
      @click="emit('select', cat)"
    >
      <div class="cat-thumb-wrap">
        <img class="cat-thumb" :src="catImg(cat)" :alt="cat.title" loading="lazy" decoding="async" />
        <span v-if="cat.productCount != null && cat.productCount > 0" class="cat-badge">
          {{ cat.productCount > 99 ? '99+' : cat.productCount }}
        </span>
      </div>
      <span class="cat-label">{{ cat.title }}</span>
    </button>

    <div class="cat-version" aria-label="Editor-Version">
      v{{ appVersion }}
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { APP_VERSION } from '../../runtime/appVersion'

defineProps<{
  categories: Array<{
    _id: number | string
    id: string
    title: string
    thumbnail?: string | null
    thumbnailWebp?: string | null
    editorType?: string | null
    productCount?: number | null
  }>
  activeCategoryId?: number | null
  /** true = horizontaler Slider (Mobile) */
  horizontal?: boolean
  /** "Alle"-Button oben anzeigen */
  showAll?: boolean
}>()

const emit = defineEmits<{
  (e: 'select', cat: any): void
}>()

const panelRef = ref<HTMLElement | null>(null)
const appVersion = APP_VERSION

function catImg(cat: any): string {
  const src = cat.thumbnailWebp || cat.thumbnail
  if (!src) return 'https://via.placeholder.com/64x64?text=K'
  return src.startsWith('http') ? src : `https://www.pixelfoto-express.de${src}`
}

function scrollToCategory(categoryId?: number | string | null) {
  const root = panelRef.value
  if (!root) return
  const target = categoryId == null
    ? root.querySelector('[data-category-id="all"]')
    : root.querySelector(`[data-category-id="${String(categoryId)}"]`)
  if (!(target instanceof HTMLElement)) return
  target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
}

defineExpose({ scrollToCategory })
</script>

<style scoped>
/* ── Shared ─────────────────────────────────────────────────── */
.cat-panel {
  box-sizing: border-box;
  background: #fff;
}

.cat-version{
  color: rgba(15, 23, 42, 0.42);
  font-size: 11px;
  line-height: 1.2;
  letter-spacing: .03em;
  user-select: text;
  border-top: 1px solid rgba(15, 23, 42, 0.1);
}

.cat-thumb-wrap {
  position: relative;
  flex-shrink: 0;
}

.cat-thumb {
  display: block;
  object-fit: cover;
  background: rgba(15, 23, 42, 0.04);
}

.cat-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  padding: 0 3px;
  border-radius: 999px;
  background: #e30b17;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  white-space: nowrap;
  box-sizing: border-box;
  pointer-events: none;
}

.cat-label {
  font-size: 13px;
  line-height: 1.25;
  text-align: center;
  word-break: break-word;
  hyphens: auto;
  color: rgba(15, 23, 42, 0.78);
}


.cat-item {
  display: flex;
  align-items: center;
  background: transparent;
  cursor: pointer;
  border: none;
  border-radius: 0;
  transition: transform 100ms ease, opacity 100ms ease;
}

.cat-item:hover { transform: translateY(-1px); }
.cat-item.active .cat-label { color: #0f172a; font-weight: 700; }

.cat-all-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15,23,42,0.06);
  border-radius: 4px;
  color: rgba(15,23,42,0.5);
}
.cat-all-icon svg { width: 52%; height: 52%; }
.cat-item--all.active .cat-all-icon {
  background: rgba(227,11,23,0.08);
  color: #e30b17;
}

/* ── Vertikal (Desktop links) ───────────────────────────────── */
.cat-panel:not(.horizontal) {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 12px 24px;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  border-right: 1px solid rgba(15, 23, 42, 0.06);
  scrollbar-width: thin;
  scrollbar-color: rgba(15, 23, 42, 0.14) transparent;
}

.cat-panel:not(.horizontal) .cat-version{
  margin-top: auto;
  padding: 10px 6px 0;
  text-align: center;
}

.cat-panel:not(.horizontal) .cat-item {
  flex-direction: column;
  gap: 10px;
  padding: 8px 6px 10px;
  width: 100%;
  min-width: 0;
  border: 1px solid transparent;
  border-radius: 6px;
}

.cat-panel:not(.horizontal) .cat-thumb {
  width: 84px;
  aspect-ratio: 3 / 2;
  height: auto;
  border-radius: 0;
}

.cat-panel:not(.horizontal) .cat-label {
  max-width: 96px;
  font-size: 14px;
}

.cat-panel:not(.horizontal) .cat-item.active {
  background: #fff;
  border-color: #e30b17;
  box-shadow: 0 1px 4px rgba(227, 11, 23, 0.12);
}

/* ── Horizontal (Mobile Slider) ─────────────────────────────── */
.cat-panel.horizontal {
  display: flex;
  flex-direction: row;
  gap: 10px;
  padding: calc(10px + env(safe-area-inset-top, 0px)) 14px 10px;
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  /* Scroll-Snap für sanftes Gleiten */
  scroll-snap-type: x mandatory;
  width: 100%;
}

.cat-panel.horizontal .cat-version{
  display: none;
}

.cat-panel.horizontal::-webkit-scrollbar { display: none; }

.cat-panel.horizontal .cat-item {
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 6px 4px 8px;
  flex-shrink: 0;
  scroll-snap-align: start;
  min-width: 70px;
  border: 1px solid transparent;
  border-radius: 6px;
}

.cat-panel.horizontal .cat-thumb {
  width: 62px;
  aspect-ratio: 3 / 2;
  height: auto;
  border-radius: 0;
}

.cat-panel.horizontal .cat-label {
  max-width: 76px;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  hyphens: none;
}

.cat-panel.horizontal .cat-item.active {
  background: #fff;
  border-color: #e30b17;
  box-shadow: 0 1px 4px rgba(227, 11, 23, 0.12);
}
</style>
